import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    Signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
    CancellationModel,
    ConfigConstants,
    DashboardButtonService,
    entryToNotificationModel,
    EscalationInstanceToken,
    EscalationInterface,
    MaterialService,
    ModalClass,
    NOTIFICATION_STATUS_CODE,
    NotificationCardState,
    NotificationIdentifierModel,
    NotificationIdState,
    notificationIdToState,
    NotificationUpdateService,
    PaceCoreModule,
    selectCancellationConfirmedById,
    selectCardStateById,
    selectDisplayEtaAlert,
    selectFirstEntryInProgress,
    selectIsInProgressById,
    selectNewCardStates,
    selectNotificationEntries,
    selectNotificationAddressesById,
    selectNotificationEntryById,
    selectTodaysCardStates,
    selectUpcomingCardStates,
    showNoEquipmentNotificationList,
    stateToNotificationIdModel,
} from '@evorait/pace-core';
import { PaceNotificationCard, PaceNotificationCardListComponent } from '@evorait/pace-components';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TabsetComponent, TabsModule } from 'ngx-bootstrap/tabs';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AlertService } from '../../../core/services/eta-alert/alert.service';
import {
    CancellationComponent,
    ICancellationResult,
} from '../../../shared/components/cancellation/cancellation.component';
import { DASHBOARD_TAB } from '../dashboard/dashboard.model';
import { DashboardNotificationCardFactory } from './dashboard-notification-card.factory';
import { CANCELLATION_PENDING_STATUS_CODES, DashboardView } from './dahboard-ngrx.interface';

/**
 * The dashboard: today's, the upcoming and the new notifications, one column each. Routed behind the
 * `environment.enableNgRx` flag.
 *
 * It reads its notifications exclusively from the notification store slice — no
 * `NotificationDataInterface` anywhere — and renders them through the presentational
 * `pace-notification-card-list` / `pace-notification-card` components from
 * `@evorait/pace-components`. Everything the store projection leaves out (formatting,
 * translations, config- and material-dependent fields) is added by
 * {@link DashboardNotificationCardFactory}.
 *
 * This component deliberately does not set the store's "current notification": its status badge is
 * bound to the card state directly, so there is nothing that would need re-triggering.
 */
@Component({
    selector: 'pace-dashboard-ngrx',
    standalone: true,
    imports: [PaceNotificationCardListComponent, TabsModule, PaceCoreModule, TranslateModule],
    templateUrl: './dashboard-ngrx.component.html',
    styleUrls: ['./dashboard-ngrx.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardNgrxComponent implements OnInit, AfterViewInit {
    private readonly tabSet = viewChild<TabsetComponent>('tabSet');

    private readonly store = inject(Store);
    private readonly log: LogInterface = inject(LogInstanceToken);
    private readonly translate = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly modalService = inject(BsModalService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly cardFactory = inject(DashboardNotificationCardFactory);
    private readonly escalationService = inject<EscalationInterface>(EscalationInstanceToken);
    private readonly notificationUpdateService = inject(NotificationUpdateService);
    private readonly materialService = inject(MaterialService);
    private readonly alertService = inject(AlertService);
    private readonly dashboardButtonService = inject(DashboardButtonService);

    private readonly displayEtaAlert = this.store.selectSignal(selectDisplayEtaAlert);

    public readonly noResultsLabel = <string>this.translate.instant('DASHBOARD_NO_RESULTS_LABEL');

    private readonly view$: Observable<DashboardView> = this.store.select(selectNotificationEntries).pipe(
        // "now" is re-read whenever the notification collection changes, which is when a
        // notification can move between the today/upcoming columns. A status change flows through
        // the inner selectors and needs no new timestamp.
        switchMap(() => {
            const nowMs = Date.now();
            return combineLatest({
                today: this.store.select(selectTodaysCardStates(nowMs)),
                upcoming: this.store.select(selectUpcomingCardStates(nowMs)),
                newAssignments: this.store.select(selectNewCardStates),
            });
        }),
        map((columns) => this.toView(columns)),
        // One projection for all readers (the view signal, the alert check, the tab selection);
        // ref-counted so nothing keeps this component subscribed to the store after it is gone.
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly view: Signal<DashboardView> = toSignal(this.view$, {
        initialValue: this.toView({ today: [], upcoming: [], newAssignments: [] }),
    });

    public readonly today: Signal<PaceNotificationCard[]> = computed(() => {
        return this.view().today;
    });
    public readonly upcoming: Signal<PaceNotificationCard[]> = computed(() => {
        return this.view().upcoming;
    });
    public readonly newAssignments: Signal<PaceNotificationCard[]> = computed(() => {
        return this.view().newAssignments;
    });

    private readonly activeTabSubject = new BehaviorSubject<DASHBOARD_TAB>(DASHBOARD_TAB.TODAYS);
    public readonly activeTab$: Observable<DASHBOARD_TAB> = this.activeTabSubject.asObservable();
    private readonly inProgressId$: Observable<string | undefined> = this.store.select(selectFirstEntryInProgress).pipe(
        // The card handle, not the store identifier: what it is compared against is `card.id`.
        map((entry) => (entry ? stateToNotificationIdModel(entry.notificationId).toString() : undefined)),
        distinctUntilChanged(),
    );
    /** The two inputs the active tab is picked from: the column counts and what is in progress. */
    private readonly tabInput$ = combineLatest([this.view$, this.inProgressId$]);

    public constructor() {
        this.materialService.updateUnprocessedMaterials().pipe(take(1)).subscribe();
    }

    public ngOnInit(): void {
        // show the no Equipment notification list on dashboard init
        this.store.dispatch(showNoEquipmentNotificationList({ showAppNotification: true }));

        // Escalation timers and the ETA alert follow the notification data, so they are re-checked
        // whenever it changes. Both checks are idempotent.
        this.view$
            .pipe(
                tap(() => this.checkAlerts()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.tabInput$
            .pipe(
                tap(([view, inProgressId]) => this.updateActiveTab(view, inProgressId, false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        // The dashboard button asks for the new-assignments tab. Its subject replays the latest
        // request, so subscribing here also performs the initial tab selection — and because this
        // subscription is set up after the one above, a pending request wins, as it did before.
        this.dashboardButtonService.onRedirectToDashboard$
            .pipe(
                // A request can arrive before the store holds any notification; taking the first
                // emission of the replayed input defers the decision until the data is there.
                switchMap((isRedirected) =>
                    this.tabInput$.pipe(
                        take(1),
                        map(([view, inProgressId]) => ({ view, inProgressId, isRedirected })),
                    ),
                ),
                tap(({ view, inProgressId, isRedirected }) => this.updateActiveTab(view, inProgressId, isRedirected)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    public ngAfterViewInit(): void {
        this.activeTabSubject
            .pipe(
                tap((active: DASHBOARD_TAB) => {
                    const tabSet = this.tabSet();
                    if (!tabSet) {
                        return;
                    }
                    tabSet.tabs[active].active = true;
                    this.changeDetectorRef.detectChanges();
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    public onCardSelect(card: PaceNotificationCard): void {
        this.openSelectedNotification(
            notificationIdToState(NotificationIdentifierModel.fromString(card.id)),
            'error opening notification',
        );
    }

    /**
     * Fire-and-forget wrapper around `openNotification`, which is async because it re-reads the
     * store and may have to await the cancellation confirmation.
     *
     * @param notificationId the notification to open
     * @param errorContext what to log when opening it fails
     */
    private openSelectedNotification(notificationId: NotificationIdState, errorContext: string): void {
        this.openNotification(notificationId).catch((error) =>
            this.log.error(this.constructor.name, errorContext, error),
        );
    }

    /**
     * Opens a notification: the details screen, or the cancellation dialog while a cancellation
     * is still waiting to be confirmed.
     *
     * @param notificationId the selected notification
     */
    private async openNotification(notificationId: NotificationIdState): Promise<void> {
        const state = await firstValueFrom(this.store.select(selectCardStateById(notificationId)));
        if (!state) {
            this.log.error(
                this.constructor.name,
                `notification ${stateToNotificationIdModel(notificationId).toString()} is no longer in the store`,
            );
            return;
        }

        if (await this.isCancellationPending(state)) {
            await this.openCancellation(state);
            return;
        }
        await this.navigateToDetails(state.notificationId);
    }

    private async isCancellationPending(state: NotificationCardState): Promise<boolean> {
        if (CANCELLATION_PENDING_STATUS_CODES.has(state.statusCode)) {
            return true;
        }
        if (state.isCancelled.length === 0) {
            return false;
        }
        // A cancelled notification that is in progress may already have been confirmed.
        const confirmed = this.store.selectSignal(selectCancellationConfirmedById(state.notificationId))();
        return !confirmed;
    }

    private async openCancellation(state: NotificationCardState): Promise<void> {
        const entry = await firstValueFrom(this.store.select(selectNotificationEntryById(state.notificationId)));
        const addresses = await firstValueFrom(
            this.store.select(selectNotificationAddressesById(state.notificationId)),
        );
        if (!entry || !addresses) {
            return;
        }
        // `CancellationModel` and the cancellation dialog both take a `NotificationModel`; the
        // store's converter builds one for them, so this component still reads only from the store.
        const notification = entryToNotificationModel(entry, addresses.customer, addresses.addressSet);
        const cancellationModel = await CancellationModel.create(
            notification,
            this.notificationUpdateService.getNotificationUpdateKeyByIdForCancellation(notification.identifier),
        );
        const modalInstance = this.modalService.show(CancellationComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: cancellationModel,
            },
            ariaLabelledBy: 'cancellation-modal-title',
        });

        modalInstance.content?.modalResult
            .pipe(
                tap((result: ICancellationResult) => {
                    if (result && result.notificationUpdateKey) {
                        this.notificationUpdateService.removeUpdate(result.notificationUpdateKey);
                    }
                }),
                switchMap(() => this.store.select(selectIsInProgressById(state.notificationId)).pipe(take(1))),
                filter(Boolean),
                // Route to details screen so that user can send confirmation of in-progress
                // notification. Re-entering `openNotification` re-reads the store, so the now
                // confirmed cancellation lets it through to the details screen.
                tap(() =>
                    this.openSelectedNotification(
                        state.notificationId,
                        'error opening notification after cancellation',
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    private async navigateToDetails(notificationId: NotificationIdState): Promise<void> {
        await this.router.navigate([
            ConfigConstants.routeUrlDetails,
            notificationId.frontId,
            notificationId.qmNum,
            notificationId.maNum,
        ]);
    }

    private checkAlerts(): void {
        // check if any notification is in progress and set the timer accordingly to raise the escalation alert
        this.escalationService.checkEscalationRequired();
        if (this.displayEtaAlert()) {
            this.alertService.checkEtaAlertRequired();
        }
    }

    private toView(columns: {
        today: NotificationCardState[];
        upcoming: NotificationCardState[];
        newAssignments: NotificationCardState[];
    }): DashboardView {
        return {
            today: this.toColumn(columns.today),
            upcoming: this.toColumn(columns.upcoming),
            newAssignments: this.toColumn(columns.newAssignments),
        };
    }

    /**
     * A confirmed cancellation (CNCL) still counts towards the column's total, but is not rendered:
     * there is nothing left for the user to do with it.
     *
     * @param states the column's notifications
     * @returns the column's count and its rendered cards
     */
    private toColumn(states: NotificationCardState[]): PaceNotificationCard[] {
        return states
            .filter((state) => state.statusCode !== NOTIFICATION_STATUS_CODE.CNCL)
            .map((state) => this.cardFactory.create(state));
    }

    /**
     * Picks the tab to show: a redirect request or nothing in progress opens the new assignments, an
     * in-progress notification opens the column it sits in, and otherwise the first non-empty column
     * wins. When every column is empty the current tab stays.
     *
     * @param view the current columns
     * @param inProgressId id of the notification in progress, if any
     * @param isRedirected whether the dashboard button asked for the new assignments
     */
    private updateActiveTab(view: DashboardView, inProgressId: string | undefined, isRedirected: boolean): void {
        const tab = this.resolveActiveTab(view, inProgressId, isRedirected);
        if (tab !== undefined) {
            this.activeTabSubject.next(tab);
        }
    }

    private resolveActiveTab(
        view: DashboardView,
        inProgressId: string | undefined,
        isRedirected: boolean,
    ): DASHBOARD_TAB | undefined {
        if ((isRedirected || !inProgressId) && view.newAssignments.length > 0) {
            return DASHBOARD_TAB.NEW;
        }
        if (inProgressId) {
            const isTodays = view.today.some((card) => card.id === inProgressId);
            return isTodays ? DASHBOARD_TAB.TODAYS : DASHBOARD_TAB.UPCOMING;
        }
        if (view.today.length > 0) {
            return DASHBOARD_TAB.TODAYS;
        }
        if (view.upcoming.length > 0) {
            return DASHBOARD_TAB.UPCOMING;
        }
        if (view.newAssignments.length > 0) {
            return DASHBOARD_TAB.NEW;
        }
        return undefined;
    }
}
